import React, { useState, useEffect, useCallback } from 'react';
import { studentsAPI, consentRequestsAPI, checklistItemsAPI } from '../services/api';
import VisaProfilePanel from './VisaProfilePanel';
import DenklikProfilePanel from './DenklikProfilePanel';
import ChecklistLinkPanel from './ChecklistLinkPanel';
import PaymentsPanel from './PaymentsPanel';

const GROUP_LABELS = {
  APPLICATION: 'Application', VISA: 'Visa Processing',
  RESIDENCE_PERMIT: 'Residence Permit', PLANE_TICKET: 'Plane Ticket Booking',
  EQUIVALENCE_LETTER: 'Equivalence Letter (Denklik)',
};
const GROUPS = ['APPLICATION', 'VISA', 'RESIDENCE_PERMIT', 'PLANE_TICKET', 'EQUIVALENCE_LETTER'];

const STATUS_STYLES = {
  MISSING: { bg: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', label: 'Missing' },
  UPLOADED_AWAITING_CHECK: { bg: 'rgba(245,166,35,0.15)', color: '#f5a623', label: 'Waiting for VEC check' },
  RECEIVED: { bg: 'rgba(74,222,128,0.15)', color: '#4ade80', label: 'Received' },
  REJECTED: { bg: 'rgba(245,87,108,0.15)', color: '#f5576c', label: 'Rejected' },
  NOT_NEEDED: { bg: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)', label: 'Not needed' },
};

// Opaque dark card (not the translucent rgba(15,52,96,0.25) used by the
// older KVKK/App-Login sections elsewhere in this modal) — this modal's
// background is plain white, so a translucent navy card washes light text
// out to near-illegible. Matches the Quest Map Progress card just above,
// which uses the same solid gradient and reads fine.
const panelStyle = {
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  marginBottom: 12,
};
const headingStyle = { color: '#9aa4c4', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 };

// Partner progress tracking (brainstorms/2026-09-27-partner-progress-tracking.md):
// consent status/actions + the 8-item document checklist a partner sees on
// their own portal once consent is given. `hasReferringPartner` is whether
// assigned_agent is itself an AGENT (the referring partner) — the checklist
// still shows for in-house students, just without the consent block.
const PartnerProgressPanel = ({
  studentId, hasReferringPartner, initialConsentStatus, waitingOnOverride, onWaitingOnOverrideChange,
}) => {
  const [checklist, setChecklist] = useState([]);
  const [loadingChecklist, setLoadingChecklist] = useState(true);
  const [consentStatus, setConsentStatus] = useState(initialConsentStatus);
  const [consentLink, setConsentLink] = useState(null);
  const [sendingLink, setSendingLink] = useState(false);
  const [recordingVerbal, setRecordingVerbal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [busyItemId, setBusyItemId] = useState(null);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadChecklist = useCallback(async () => {
    if (!studentId) return;
    setLoadingChecklist(true);
    try {
      const res = await studentsAPI.checklist(studentId);
      setChecklist(res.data || []);
    } catch (err) {
      setError('Could not load the document checklist.');
    } finally {
      setLoadingChecklist(false);
    }
  }, [studentId]);

  useEffect(() => { loadChecklist(); }, [loadChecklist]);
  useEffect(() => { setConsentStatus(initialConsentStatus); }, [initialConsentStatus]);

  const handleSendLink = async () => {
    setSendingLink(true);
    setError('');
    try {
      const res = await consentRequestsAPI.sendLink(studentId);
      setConsentLink({ link: res.data.link, whatsapp_url: res.data.whatsapp_url });
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create a consent link.');
    } finally {
      setSendingLink(false);
    }
  };

  const handleRecordVerbal = async () => {
    if (!window.confirm(
      "Confirm the student (or guardian, if under 18) gave verbal/written consent to share progress with the referring partner?"
    )) return;
    setRecordingVerbal(true);
    setError('');
    try {
      await consentRequestsAPI.recordVerbal(studentId);
      setConsentStatus('GIVEN');
    } catch (err) {
      setError('Could not record consent.');
    } finally {
      setRecordingVerbal(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm("Withdraw this student's consent to share progress with the referring partner?")) return;
    setError('');
    try {
      await consentRequestsAPI.withdraw(studentId);
      setConsentStatus('PENDING');
    } catch (err) {
      setError('Could not withdraw consent.');
    }
  };

  const handleAccept = async (item) => {
    setBusyItemId(item.id);
    setError('');
    try {
      await checklistItemsAPI.accept(item.id);
      await loadChecklist();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not accept this upload.');
    } finally {
      setBusyItemId(null);
    }
  };

  const handleReject = async (item) => {
    if (!rejectNote.trim()) return;
    setBusyItemId(item.id);
    setError('');
    try {
      await checklistItemsAPI.reject(item.id, rejectNote.trim());
      setRejectingId(null);
      setRejectNote('');
      await loadChecklist();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not reject this upload.');
    } finally {
      setBusyItemId(null);
    }
  };

  const handleMarkNotNeeded = async (item) => {
    setBusyItemId(item.id);
    setError('');
    try {
      await checklistItemsAPI.markNotNeeded(item.id);
      await loadChecklist();
    } catch (err) {
      setError('Could not update this item.');
    } finally {
      setBusyItemId(null);
    }
  };

  const handleStaffUpload = async (item, file) => {
    if (!file) return;
    setBusyItemId(item.id);
    setError('');
    try {
      await checklistItemsAPI.upload(item.id, file);
      await loadChecklist();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not upload this file.');
    } finally {
      setBusyItemId(null);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    setError('');
    try {
      const res = await studentsAPI.checklistPdf(studentId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Document-Requirements-${studentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Could not download the requirements checklist.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div style={{ margin: '16px 0 0' }}>
      {hasReferringPartner && (
        <div style={panelStyle}>
          <div style={headingStyle}>SHARE PROGRESS WITH REFERRING PARTNER</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
              background: consentStatus === 'GIVEN' ? 'rgba(74,222,128,0.15)' : 'rgba(245,166,35,0.15)',
              color: consentStatus === 'GIVEN' ? '#4ade80' : '#f5a623',
            }}>
              {consentStatus === 'GIVEN' ? '✓ Consent given' : 'Awaiting consent'}
            </span>
            {consentStatus !== 'GIVEN' && (
              <>
                <button type="button" onClick={handleSendLink} disabled={sendingLink}
                  style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#333', fontSize: 12, cursor: sendingLink ? 'wait' : 'pointer' }}>
                  {sendingLink ? 'Sending…' : 'Send consent link'}
                </button>
                <button type="button" onClick={handleRecordVerbal} disabled={recordingVerbal}
                  style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#ddd', fontSize: 12, cursor: recordingVerbal ? 'wait' : 'pointer' }}>
                  {recordingVerbal ? 'Recording…' : 'Record verbal/written consent'}
                </button>
              </>
            )}
            {consentStatus === 'GIVEN' && (
              <button type="button" onClick={handleWithdraw}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #f5576c55', background: 'rgba(245,87,108,0.08)', color: '#f5576c', fontSize: 12, cursor: 'pointer' }}>
                Withdraw consent
              </button>
            )}
          </div>
          {consentLink && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input readOnly value={consentLink.link} onFocus={(e) => e.target.select()}
                style={{ flex: 1, minWidth: 220, padding: '7px 10px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#f8f9fa', color: '#333', fontSize: 12 }} />
              <button type="button" onClick={() => navigator.clipboard?.writeText(consentLink.link)}
                style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#333', fontSize: 13, cursor: 'pointer' }}>
                Copy
              </button>
              {consentLink.whatsapp_url && (
                <a href={consentLink.whatsapp_url} target="_blank" rel="noreferrer"
                  style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #25D366', background: '#25D36622', color: '#25D366', fontSize: 13, textDecoration: 'none' }}>
                  Send on WhatsApp
                </a>
              )}
            </div>
          )}
          <p style={{ color: '#8a94b8', fontSize: 11, margin: '8px 0 0' }}>
            Until the student (or guardian, if under 18) consents, the referring partner sees only the student's name.
          </p>
        </div>
      )}

      <div style={panelStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={headingStyle}>DOCUMENT CHECKLIST</div>
          <button type="button" onClick={handleDownloadPdf} disabled={downloadingPdf}
            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#9aa4c4', fontSize: 11, cursor: downloadingPdf ? 'wait' : 'pointer' }}>
            {downloadingPdf ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
        {hasReferringPartner && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: '#9aa4c4', display: 'block', marginBottom: 4 }}>
              "Waiting on" note shown to the partner (leave blank to auto-derive from the next step)
            </label>
            <input
              type="text"
              value={waitingOnOverride || ''}
              onChange={(e) => onWaitingOnOverrideChange(e.target.value)}
              placeholder="e.g. Waiting for the student's bank statement"
              style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#eee', fontSize: 12, boxSizing: 'border-box' }}
            />
          </div>
        )}
        {error && <div style={{ color: '#f5576c', fontSize: 12, marginBottom: 8 }}>{error}</div>}
        {loadingChecklist ? (
          <p style={{ color: '#9aa4c4', fontSize: 12 }}>Loading…</p>
        ) : (
          GROUPS.map((group) => (
            <div key={group} style={{ marginBottom: 10 }}>
              <div style={{ color: '#8a94b8', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{GROUP_LABELS[group]}</div>
              {checklist.filter((i) => i.group === group).map((item) => {
                const st = STATUS_STYLES[item.status] || STATUS_STYLES.MISSING;
                return (
                  <div key={item.id} style={{ padding: '8px 10px', marginBottom: 4, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#ddd', fontSize: 13 }}>{item.item_display}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
                    </div>
                    {item.status === 'UPLOADED_AWAITING_CHECK' && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {(item.student_upload_url || item.partner_upload_url) && (
                          <a href={item.student_upload_url || item.partner_upload_url} target="_blank" rel="noreferrer" style={{ color: '#4facfe', fontSize: 12 }}>
                            View upload
                            {item.pending_upload_source === 'student' ? ' (uploaded by student)' : ''}
                            {item.pending_upload_source === 'partner' ? ' (uploaded by partner)' : ''}
                            {item.partner_upload_name || item.student_upload_name ? ` — ${item.partner_upload_name || item.student_upload_name}` : ''}
                          </a>
                        )}
                        <button type="button" disabled={busyItemId === item.id} onClick={() => handleAccept(item)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#4ade80', color: '#023', fontSize: 12, cursor: busyItemId === item.id ? 'wait' : 'pointer', fontWeight: 600 }}>
                          Accept
                        </button>
                        {rejectingId === item.id ? (
                          <>
                            <input type="text" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                              placeholder="Reason shown to the partner"
                              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#eee', fontSize: 12, minWidth: 160 }} />
                            <button type="button" disabled={busyItemId === item.id || !rejectNote.trim()} onClick={() => handleReject(item)}
                              style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#f5576c', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                              Confirm reject
                            </button>
                            <button type="button" onClick={() => { setRejectingId(null); setRejectNote(''); }}
                              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#aaa', fontSize: 12, cursor: 'pointer' }}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button type="button" disabled={busyItemId === item.id} onClick={() => setRejectingId(item.id)}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #f5576c55', background: 'transparent', color: '#f5576c', fontSize: 12, cursor: 'pointer' }}>
                            Reject
                          </button>
                        )}
                      </div>
                    )}
                    {item.status === 'REJECTED' && item.rejection_note && (
                      <div style={{ marginTop: 4, color: '#f5a623', fontSize: 11 }}>Note: {item.rejection_note}</div>
                    )}
                    {item.staff_upload_url && (
                      <div style={{ marginTop: 4 }}>
                        <a href={item.staff_upload_url} target="_blank" rel="noreferrer" style={{ color: '#4facfe', fontSize: 12 }}>
                          View VEC upload {item.staff_upload_name ? `(${item.staff_upload_name})` : ''}
                        </a>
                      </div>
                    )}
                    {(item.status === 'MISSING' || item.status === 'REJECTED') && (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {item.status === 'MISSING' && (
                          <button type="button" disabled={busyItemId === item.id} onClick={() => handleMarkNotNeeded(item)}
                            style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#9aa4c4', fontSize: 11, cursor: 'pointer' }}>
                            Mark not needed
                          </button>
                        )}
                        <label style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#9aa4c4', fontSize: 11, cursor: busyItemId === item.id ? 'wait' : 'pointer' }}>
                          Upload from VEC
                          <input type="file" style={{ display: 'none' }} disabled={busyItemId === item.id}
                            onChange={(e) => handleStaffUpload(item, e.target.files?.[0])} />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <VisaProfilePanel studentId={studentId} onProfileChanged={loadChecklist} />
      <DenklikProfilePanel studentId={studentId} />
      <ChecklistLinkPanel studentId={studentId} />
      <PaymentsPanel studentId={studentId} />
    </div>
  );
};

export default PartnerProgressPanel;
